import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { flow, isEmpty } from 'lodash-es';
import { connect } from 'react-redux';
import { PersonalizedSearchModel, PersonalizedSearchPaths } from 'client/data/models/personalized-search';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { PageModel } from 'client/data/models/page';
import {
  SEARCH_KEY_QUERY_PARAM,
  SEARCH_QUERY_PARAM,
} from 'site-modules/shared/constants/personalized-search/personalized-search';
import { SUPPORTED_COMPONENTS } from 'site-modules/personalized-search/constants/supported-components';
import { COMPONENTS } from 'site-modules/personalized-search/constants/components';
import { SearchResultsContext } from 'site-modules/personalized-search/context/search-results-context/search-results-context';

const STREAMABLE_COMPONENTS = [];

const withPageContext = component =>
  connectToModel(component, {
    location: bindToPath('location', PageModel, null, false),
  });

const withLLMSearchSubmitParams = flow(
  component =>
    connectToModel(component, {
      llmSearchSubmitParams: bindToPath(
        () => PersonalizedSearchPaths.getLlmSearchSubmitParams(),
        PersonalizedSearchModel,
        null,
        false
      ),
    }),
  connect(state => ({
    isMobile: state.mobile,
  }))
);

const withPageStructures = flow(component =>
  connectToModel(component, {
    supposedPageStruct: bindToPath(
      props => {
        const { llmSearchSubmitParams } = props;

        const supposedPageStructId = llmSearchSubmitParams?.extra?.find(
          extraItem => extraItem.name === 'components'
        )?.id;

        return supposedPageStructId ? PersonalizedSearchPaths.getSupposedPageStruct(supposedPageStructId) : null;
      },
      PersonalizedSearchModel,
      null,
      false
    ),
    pageStruct: bindToPath(
      props => {
        const { location } = props;

        function getPath() {
          return PersonalizedSearchPaths.getPageStruct;
        }

        const path = getPath();

        return location?.query?.[SEARCH_KEY_QUERY_PARAM] ? path(location.query[SEARCH_KEY_QUERY_PARAM]) : null;
      },
      PersonalizedSearchModel,
      null,
      false
    ),
  })
);

const initialPageStructureObj = () => ({
  stub: true,
  components: [SUPPORTED_COMPONENTS.overview],
});

function getInitialPageStructure({ location }) {
  const query = location?.query ?? {};
  const cacheQueryParam = query[SEARCH_QUERY_PARAM];
  const cacheKeyParam = query[SEARCH_KEY_QUERY_PARAM];

  return !!cacheQueryParam || !!cacheKeyParam ? initialPageStructureObj() : {};
}

export function SearchResultsContextProviderUI({
  pageStruct: pageStructProp,
  supposedPageStruct = {},
  llmSearchSubmitParams,
  location = {},
  children,
}) {
  const input = pageStructProp?.input || location.query?.[SEARCH_QUERY_PARAM] || '';
  const initialPageStructure = useMemo(
    () =>
      getInitialPageStructure({
        location,
      }),
    [location]
  );

  const streamableComponentsStructure = useMemo(() => {
    const streamableComponents = llmSearchSubmitParams?.extra?.filter(component =>
      STREAMABLE_COMPONENTS.includes(component.name)
    );

    if (!streamableComponents?.length) {
      return { components: [] };
    }

    return streamableComponents.reduce(
      (acc, component) => ({
        [component.name]: {
          id: component.id,
        },
        components: [...acc.components, component.name],
      }),
      { components: [] }
    );
  }, [llmSearchSubmitParams?.extra]);

  const searchId = location.query?.[SEARCH_KEY_QUERY_PARAM] || llmSearchSubmitParams?.id;
  const receivedComponents = pageStructProp?.struct?.components ?? [];
  const isInventoryOnly = receivedComponents.length === 1 && receivedComponents[0] === COMPONENTS.INVENTORY;

  const pageStruct = useMemo(() => {
    if (pageStructProp === null || llmSearchSubmitParams === null) {
      return null;
    }

    if (!(supposedPageStruct?.struct || pageStructProp?.struct)) {
      return {
        ...streamableComponentsStructure,
        ...initialPageStructure,
        components: Array.from(
          new Set([...streamableComponentsStructure.components, ...(initialPageStructure.components ?? [])])
        ),
      };
    }

    if (!pageStructProp?.struct) {
      return {
        ...streamableComponentsStructure,
        ...supposedPageStruct.struct,
        components: Array.from(
          new Set([
            ...(streamableComponentsStructure.components ?? []),
            ...(supposedPageStruct.struct?.components ?? []),
          ])
        ),
      };
    }

    if (isEmpty(pageStructProp?.struct)) {
      return {};
    }

    return {
      ...streamableComponentsStructure,
      ...pageStructProp?.struct,
      components: Array.from(new Set([...streamableComponentsStructure.components, ...receivedComponents])),
    };
  }, [
    llmSearchSubmitParams,
    pageStructProp,
    streamableComponentsStructure,
    supposedPageStruct,
    initialPageStructure,
    receivedComponents,
  ]);
  const isInventoryFirst = pageStruct?.components?.[0] === COMPONENTS.INVENTORY;

  const initialContextValue = useMemo(
    () => ({
      searchId,
      pageStruct,
      input,
      searchSubmitParams: llmSearchSubmitParams,
      isPageStructLoading: !!pageStruct?.stub,
      isPageStructReceived: !!pageStructProp?.struct,
      location,
      isInventoryFirst,
      isInventoryOnly,
    }),
    [
      searchId,
      pageStruct,
      input,
      llmSearchSubmitParams,
      pageStructProp?.struct,
      location,
      isInventoryFirst,
      isInventoryOnly,
    ]
  );

  return <SearchResultsContext.Provider value={initialContextValue}>{children}</SearchResultsContext.Provider>;
}

SearchResultsContextProviderUI.displayName = 'SearchResultsContextProviderUI';
SearchResultsContextProviderUI.propTypes = {
  children: PropTypes.node.isRequired,
  pageStruct: PropTypes.shape({}),
  supposedPageStruct: PropTypes.shape({}),
  llmSearchSubmitParams: PropTypes.shape({}),
  location: PropTypes.shape({}),
};

export const SearchResultsContextProvider = flow(
  withPageStructures,
  withLLMSearchSubmitParams,
  withPageContext
)(SearchResultsContextProviderUI);
